import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Tag, Button, List, Spin, Alert, Badge, Modal, Descriptions, Divider } from 'antd';
import { 
  CheckOutlined, 
  CrownOutlined, 
  StarOutlined,
  UserOutlined,
  DatabaseOutlined,
  ApiOutlined,
  TeamOutlined,
  EyeOutlined,
  DollarOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

const PlanSelection = ({ selectedPlan, onPlanSelect }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the admin server URL for subscription plans
      const adminApiUrl = process.env.REACT_APP_API_URL_ADMIN;
      console.log('🔍 Fetching plans from:', `${adminApiUrl}/subscription-plans/public/active`);
      
      const response = await axios.get(`${adminApiUrl}/subscription-plans/public/active`);
      console.log('✅ Plans response:', response.data);
      
      if (response.data.success && response.data.data && response.data.data.plans) {
        const fetchedPlans = response.data.data.plans;
        console.log('📊 Fetched plans:', fetchedPlans.length, 'plans');
        setPlans(fetchedPlans);
        
        if (fetchedPlans.length === 0) {
          setError('No subscription plans available at the moment.');
        }
      } else {
        console.error('❌ Invalid response format:', response.data);
        setError('Invalid response from server. Please try again.');
      }
    } catch (err) {
      console.error('❌ Failed to fetch plans:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError(`Failed to load subscription plans: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const showPlanDetails = (plan) => {
    setSelectedPlanDetails(plan);
    setDetailModalVisible(true);
  };

  const handlePlanSelect = (planId) => {
    onPlanSelect(planId);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <Spin size="large" />
        <div className="mt-4">
          <Text>Loading subscription plans...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={fetchPlans}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <div className="w-full">
      <div className="text-center mb-8">
        <Title level={3} className="text-gray-900 dark:text-gray-100 mb-2">
          Choose Your Plan
        </Title>
        <Text className="text-gray-600 dark:text-gray-400 text-lg">
          Select the perfect plan for your business needs
        </Text>
      </div>

      {/* Plan List */}
      <div className="space-y-4 w-full mb-10">
        {plans.map((plan) => (
          <div
            key={plan._id}
            className={`border rounded-lg p-4 sm:p-6 transition-all duration-200 cursor-pointer ${
              selectedPlan === plan._id 
                ? 'border-blue-500 bg-blue-50 shadow-md' 
                : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
            }`}
            onClick={() => handlePlanSelect(plan._id)}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-4 flex-1">
                {/* Plan Icon */}
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                  style={{ backgroundColor: plan.metadata?.color || '#1890ff' }}
                >
                  <span className="text-sm">
                    {plan.metadata?.icon || <CrownOutlined />}
                  </span>
                </div>

                {/* Plan Info */}
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-1 sm:space-y-0">
                    <Text strong className="text-lg sm:text-xl">
                      {plan.name}
                    </Text>
                    {plan.isPopular && (
                      <Tag color="orange" className="text-xs w-fit">
                        <StarOutlined /> Popular
                      </Tag>
                    )}
                  </div>
                  <Text className="text-gray-600 text-sm sm:text-base mt-1">
                    {plan.description?.substring(0, 100)}...
                  </Text>
                </div>
              </div>

              {/* Price and Actions */}
              <div className="flex items-center justify-between sm:justify-end space-x-4">
                <div className="text-left sm:text-right">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                    ${plan.price}
                  </div>
                  <div className="text-sm text-gray-500">
                    per {plan.billingPeriod}
                  </div>
                </div>

                <div className="flex space-x-2 flex-shrink-0">
                  <Button
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      showPlanDetails(plan);
                    }}
                  >
                    Details
                  </Button>
                  
                  {selectedPlan === plan._id ? (
                    <Button type="primary" size="small" icon={<CheckOutlined />}>
                      Selected
                    </Button>
                  ) : (
                    <Button size="small">
                      Select
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedPlan && (
        <div className="mt-6 mb-10 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckOutlined className="text-green-500 mr-2" />
            <Text className="text-green-700">
              Plan selected! You can change this later in your account settings.
            </Text>
          </div>
        </div>
      )}

      {/* Plan Details Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center text-white"
              style={{ backgroundColor: selectedPlanDetails?.metadata?.color || '#1890ff' }}
            >
              <span className="text-sm">
                {selectedPlanDetails?.metadata?.icon || <CrownOutlined />}
              </span>
            </div>
            <span>{selectedPlanDetails?.name}</span>
            {selectedPlanDetails?.isPopular && (
              <Tag color="orange" size="small">
                <StarOutlined /> Popular
              </Tag>
            )}
          </div>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
          <Button 
            key="select" 
            type="primary" 
            onClick={() => {
              handlePlanSelect(selectedPlanDetails._id);
              setDetailModalVisible(false);
            }}
            disabled={selectedPlan === selectedPlanDetails?._id}
          >
            {selectedPlan === selectedPlanDetails?._id ? 'Already Selected' : 'Select This Plan'}
          </Button>
        ]}
        width={600}
      >
        {selectedPlanDetails && (
          <div>
            {/* Pricing */}
            <div className="text-center mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="text-4xl font-bold text-gray-900 mb-1">
                ${selectedPlanDetails.price}
              </div>
              <div className="text-gray-600">
                per {selectedPlanDetails.billingPeriod}
              </div>
            </div>

            {/* Description */}
            <Paragraph className="text-gray-700 mb-4">
              {selectedPlanDetails.description}
            </Paragraph>

            <Divider>Plan Limits</Divider>

            {/* Plan Limits */}
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Max Users">
                <div className="flex items-center">
                  <UserOutlined className="mr-2 text-blue-500" />
                  {selectedPlanDetails.limits?.maxUsers === -1 ? 'Unlimited' : selectedPlanDetails.limits?.maxUsers || 0}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Max Storage">
                <div className="flex items-center">
                  <DatabaseOutlined className="mr-2 text-green-500" />
                  {selectedPlanDetails.limits?.maxStorage === -1 ? 'Unlimited' : `${selectedPlanDetails.limits?.maxStorage || 0}GB`}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Max Leads">
                <div className="flex items-center">
                  <TeamOutlined className="mr-2 text-purple-500" />
                  {selectedPlanDetails.limits?.maxLeads === -1 ? 'Unlimited' : selectedPlanDetails.limits?.maxLeads || 0}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Max Integrations">
                <div className="flex items-center">
                  <ApiOutlined className="mr-2 text-orange-500" />
                  {selectedPlanDetails.limits?.maxIntegrations === -1 ? 'Unlimited' : selectedPlanDetails.limits?.maxIntegrations || 0}
                </div>
              </Descriptions.Item>
            </Descriptions>

            <Divider>Features Included</Divider>

            {/* Features List */}
            <List
              dataSource={selectedPlanDetails.features || []}
              renderItem={(feature) => (
                <List.Item className="border-none py-2">
                  <List.Item.Meta
                    avatar={<CheckOutlined className="text-green-500" />}
                    title={<span className="text-sm font-medium">{feature.name}</span>}
                    description={feature.description && (
                      <span className="text-xs text-gray-600">{feature.description}</span>
                    )}
                  />
                </List.Item>
              )}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PlanSelection;
